<template>
    <base-modal
        hide-header
        hide-footer
        size="lg"
    >
        <div class="col-12">
            <h3 class="editor-header">
                {{ this.sewerLine?.id > 0 ? $t('storm_sewers.edit_sewer') : $t('storm_sewers.add_sewer') }}
            </h3>
        </div>
        <div
            class="col-sm-12 nopads px-2"
            @click.stop="null"
        >
            <div
                v-if="sewerLine"
                class="col-sm-12 editor-form"
            >
                <h4 class="form-header">
                    {{ $t('street_lights.basic_info') }}
                </h4>
                <div class="row px-1">
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('storm_sewers.status') }}</span>
                        <select
                            id="lightStatus"
                            v-model="sewerLine.status"
                            class="form-select form-select-sm"
                        >
                            <option :value="null">
                                {{ $t('street_lights.select_status') }}
                            </option>
                            <option
                                v-for="option in getStatusOptions()"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('storm_sewers.radius') }}</span>
                        <input
                            id="radius"
                            v-model="sewerLine.radius"
                            type="number"
                            step="any"
                            min="0"
                            class="form-control form-control-sm"
                        >
                    </div>
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('street_lights.construction_year') }}</span>
                        <input
                            id="lightYear"
                            v-model="sewerLine.construction_year"
                            type="number"
                            min="0"
                            class="form-control form-control-sm"
                        >
                    </div>
                </div>
                <div class="row px-1">
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('storm_sewers.material') }}</span>
                        <select
                            id="material"
                            v-model="sewerLine.material"
                            class="form-select form-select-sm sm-3"
                        >
                            <option :value="null">
                                {{ $t('road_sign.select_material') }}
                            </option>
                            <option
                                v-for="item in getMaterialOptions()"
                                :key="item.value"
                                :value="item.value"
                            >
                                {{ item.text }}
                            </option>
                        </select>
                    </div>
                    <div v-if="isLouhiSewer" class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('storm_sewers.type_sewer_type') }}</span>
                        <select
                            id="sewerLineType"
                            v-model="sewerLine.sewer_type_id"
                            class="form-select form-select-sm"
                        >
                            <option
                                value=""
                                disabled
                            >
                                {{ $t('storm_sewers.type_select_type') }}
                            </option>
                            <option
                                v-for="option in getSewerTypeOptions()"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                </div>
                <hr>
                <h4 class="form-header mt-0 pt-0">
                    {{ $t('power_stations.contract') }}
                </h4>
                <span class="tip-light">{{ $t('storm_sewers.contract_tip') }}</span>
                <open-contract-filter
                    v-if="!isLouhiSewer"
                    :to="toDate"
                    :from="fromDate"
                    :contract="contract"
                    :show-company="false"
                    :show-title="false"
                    @loading="loading=true"
                    @loading-completed="loading=false"
                    @contract-selected="setContract"
                />
                <louhi-multiple-contracts-filter
                    v-if="isLouhiSewer"
                    v-model="sewerContracts"
                    :editable="!loading"
                    :show-only-louhi-contracts="true"
                    style="margin-left: -2px; margin-right: -3px;"
                    @loading="loading = true"
                    @loading-completed="loading = false"
                    @contract-selected="setContract"
                />
                <hr>
                <div
                    v-if="!isLouhiSewer"
                    class="col-12"
                >
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4
                                class="form-header pointer hover-header"
                                @click.stop="maintenanceVisible = !maintenanceVisible"
                            >
                                {{ $t('street_lights.maintenance_info') }}
                            </h4>
                        </div>
                        <div
                            class="ms-2 px-2"
                            @click.stop="maintenanceVisible = !maintenanceVisible"
                        >
                            <font-awesome-icon
                                icon="arrow-down"
                                class="pointer no-focus arrow-icon dont-print hover-header"
                                :class="{ rotated: maintenanceVisible }"
                            />
                        </div>
                    </div>
                    <div
                        class="routa-collapse"
                        :class="{ collapsed: maintenanceVisible }"
                    >
                        <div class="col-12">
                            <span class="span-title">{{ $t('street_lights.last_maintenance') }}</span>
                            <input
                                id="bulbReplace"
                                v-model="sewerLine.last_maintenance"
                                type="date"
                                class="form-control form-control-sm"
                            >
                        </div>
                        <div class="col-12">
                            <span class="span-title">{{ $t('street_lights.info') }}</span>
                            <textarea
                                id="info"
                                v-model="sewerLine.maintenance_info"
                                class="form-control form-control-sm"
                                :placeholder="$t('street_lights.info')"
                                rows="4"
                            />
                        </div>
                    </div>
                </div>
                <hr>
                <h4 class="form-header mt-0 pt-0">
                    {{ $t('street_lights.info') }}
                </h4>
                <textarea
                    id="info"
                    v-model="sewerLine.info"
                    class="form-control form-control-sm"
                    :placeholder="$t('street_lights.info')"
                    rows="4"
                    style="resize: vertical; max-height: 8em;"
                />
                <div class="col-sm-12 button-container pt-4 pb-0 mb-0">
                    <button
                        class="btn btn-secondary form-button"
                        @click.stop="$emit('close')"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        class="btn btn-success form-button"
                        :disabled="loading"
                        @click.stop="submit"
                    >
                        {{ $t('common.save') }}
                    </button>
                </div>
            </div>
            <div
                v-if="loading"
                id="loader"
                class="spinner"
            />
        </div>
    </base-modal>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import BaseModal from '../BaseModal'
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import LouhiMultipleContractsFilter from "@/components/contract/external/LouhiMultipleContractsFilter.vue";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {inject} from "vue";
import {stormSewersMixin} from "../mixins/StormSewersMixin.js";
import {infraEquipmentHelper} from "../mixins/InfraEquipmentMixin.js";

export default {
    name: 'SewerLineWizard',
    components: {OpenContractFilter, BaseModal, LouhiMultipleContractsFilter},
    mixins: [restApi, timeUtils, stormSewersMixin, infraEquipmentHelper],
    props: {
        selectedContract: {
            type: Object,
            default: null
        },
        stormSewerLine: {
            type: Object,
            default: null
        }
    },
    emits: ['close', 'closeAndUpdate'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            sewerLine: null,
            contract: null,
            maintenanceVisible: false,
            variant: 'danger',
            sewerContracts: [],
        }
    },
    computed: {
        fromDate() {
            if (this.sewerLine && this.sewerLine.created_at) {
                return this.sewerLine.created_at
            }
            let d = new Date()
            return d.toISOString()
        },
        toDate() {
            let d = new Date()
            return d.toISOString()
        },
        isLouhiSewer() {
            if (this.sewerLine && this.sewerLine.id) {
                //existing
                return this.sewerLine.external_system === 2
            } else {
                //creating new
                return false
            }
        }
    },
    mounted() {
        this.sewerLine = JSON.parse(JSON.stringify(this.stormSewerLine))
        this.sewerLine.last_maintenance = this.sewerLine.last_maintenance ? this.timestampToDateInput(this.sewerLine.last_maintenance) : null
        this.contract = this.sewerLine.contract ? this.sewerLine.contract : null
        this.sewerContracts = this.sewerLine.contracts
        if (this.sewerLine.sewer_type_id === undefined || this.sewerLine.sewer_type_id == null) {this.sewerLine.sewer_type_id = ''}

    },
    methods: {
        submit: function () {
            this.loading = true
            if (this.contract) {
                this.sewerLine.contract = this.contract
            }

            if(this.isLouhiSewer) {
                const selectedIds = this.normalizeIds(this.sewerContracts)
                this.sewerLine.contract_ids = selectedIds
            }

            if (this.sewerLine.id && this.sewerLine.id > 0) {
                this.restUpdate(this.stormSewerUrl, this.sewerLine, this.success, this.fail)
            } else {
                this.restAdd(this.stormSewerUrl, this.sewerLine, this.success, this.fail)
            }
        },
        success: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.$emit('closeAndUpdate')
        },
        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_add_failed'))
        },
        setContract: function (contract) {
            this.contract = contract
        },

    }
}
</script>
<style>
@import "../../styles/modal.css";
</style>
